import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicShell from './PublicShell';
import Icon from '../../comps/Icon';
import publicBusinessSvc from '../../services/publicBusiness';
import { useI18n } from '../../i18n/I18nContext';
import '../../style/css/public2026.css';

// ISO-4217 → display symbol for menu prices (i18n groundwork; defaults to ₪ for ILS).
const CURRENCY_SYMBOL = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };
const currencySymbol = (c) => CURRENCY_SYMBOL[String(c || 'ILS').toUpperCase()] || '₪';

const HE_WEEK = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
// Local YYYY-MM-DD (NOT toISOString, which can shift the date by timezone).
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toMin = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + (m || 0); };
const toHHMM = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
// 30-min booking slots inside a day's opening windows (last slot 30min before close).
function slotsFromWindows(windows) {
    const out = [];
    (windows || []).forEach((w) => {
        for (let t = toMin(w.start); t + 30 <= toMin(w.end); t += 30) { out.push(toHHMM(t)); }
    });
    return [...new Set(out)].sort();
}
const DEFAULT_SLOTS = slotsFromWindows([{ start: '12:00', end: '22:30' }]);

// Map the real getPublicBusiness payload → the page's display shape. Fields with no DB
// source (tagline/phone) are left empty so the UI hides them.
function buildBiz(real, slug, t) {
    // group the flat menu ({category,name,description,price}) into [{cat, dishes:[{name,desc,price}]}]
    const order = []; const byCat = {};
    (real.menu || []).forEach((m) => {
        const cat = m.category || t('business.menu_title');
        if (!byCat[cat]) { byCat[cat] = []; order.push(cat); }
        byCat[cat].push({ name: m.name, desc: m.description, price: Number(m.price) || 0 });
    });
    const menu = order.map((cat) => ({ cat, dishes: byCat[cat] }));
    // weekly hours → display string per day + raw windows per day index (for real slots)
    const byDay = {};
    const windows = HE_WEEK.map(() => []);
    (real.hours || []).forEach((h) => {
        const idx = Number(h.dayInWeek) - 1;
        if (idx < 0 || idx > 6) return;
        const s = String(h.startTime || '').slice(0, 5); const e = String(h.endTime || '').slice(0, 5);
        byDay[idx] = byDay[idx] ? `${byDay[idx]}, ${s} – ${e}` : `${s} – ${e}`;
        windows[idx].push({ start: s, end: e });
    });
    // Day label localized (Sunday-first, matching HE_WEEK order); closed label via t.
    const WEEK_KEYS = ['search.day_sunday', 'search.day_monday', 'search.day_tuesday', 'search.day_wednesday', 'search.day_thursday', 'search.day_friday', 'search.day_saturday'];
    const hours = HE_WEEK.map((day, i) => ({ day: t(WEEK_KEYS[i]), time: byDay[i] || t('business.closed') }));
    // slots per weekday: from the real windows; if the business defined NO hours at all,
    // fall back to a default range so bookings still work (server allows any time then).
    const hasAnyHours = (real.hours || []).length > 0;
    const slotsByDay = windows.map((w) => (hasAnyHours ? slotsFromWindows(w) : DEFAULT_SLOTS));
    // "אודות" text — only if the real payload actually carries one (no invented copy).
    // The current getPublicBusiness act sends none, so this stays empty and the section hides.
    const about = String(real.about || real.description || real.tagline || '').trim();
    return {
        slug, name: real.name, cuisine: real.categories.join(' · '), area: real.city || '',
        phone: real.phone || '',   // business phone (public "call us" for oversized groups)
        currency: real.currency || 'ILS',   // ISO-4217 → menu-price symbol (i18n groundwork)
        menu, gallery: real.gallery || [], hours, slotsByDay, about,
        areas: real.areas || [],   // [{id, name}] — empty → no area selector
    };
}

// Next 14 days as chip descriptors. `dow` (today/tomorrow/short weekday) and `dnum` (day + short
// month) are localized via t + dateLocale so English chips read LTR (e.g. "Tue", "31 Jul").
function nextDays(n, t, dateLocale) {
    const out = [];
    const base = new Date();
    for (let i = 0; i < n; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        let dow;
        if (i === 0) dow = t('common.today');
        else if (i === 1) dow = t('common.tomorrow');
        else dow = d.toLocaleDateString(dateLocale, { weekday: 'short' });
        out.push({
            key: isoLocal(d),
            dow,
            dnum: d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' }),
            weekday: d.getDay(),
        });
    }
    return out;
}

// Accept an Israeli local number OR an international E.164 (+CC…) — keep the leading '+'.
const phoneValid = (p) => {
    const s = String(p).replace(/[^\d+]/g, '');
    return /^0\d{8,9}$/.test(s) || /^\+[1-9]\d{6,14}$/.test(s);
};

// "YYYY-MM-DD" → localized "<day> <month>" (e.g. "31 ביולי" / "31 July"). Empty on bad input.
function locDate(iso, dateLocale) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return '';
    try {
        const d = new Date(`${iso}T00:00:00`);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'long' });
    } catch (e) { return ''; }
}

/*
 * Parse the raw publicAvailableTimes TimeSlots into per-area availability for a party size.
 * Returns a Map keyed by areaID (number) → array of { time:"HH:MM", available:boolean },
 * one entry per slot Time (ordered). An area is AVAILABLE at a slot iff any of that slot's
 * Capacities for the area has quantity>0 AND Value>=party. Areas absent from a slot are
 * treated as unavailable at that time.
 */
function availabilityByArea(timeSlots, party) {
    const byArea = new Map();     // areaID → Map(time → available)
    const times = [];             // ordered unique "HH:MM"
    const seenTime = new Set();
    (Array.isArray(timeSlots) ? timeSlots : []).forEach((slot) => {
        if (!slot || typeof slot.Time !== 'string') return;
        const time = slot.Time.slice(0, 5);
        if (!/^\d{2}:\d{2}$/.test(time)) return;
        if (!seenTime.has(time)) { seenTime.add(time); times.push(time); }
        const areas = slot.Areas && typeof slot.Areas === 'object' ? slot.Areas : {};
        Object.keys(areas).forEach((k) => {
            const a = areas[k];
            const areaId = Number(a && a.Value);
            if (!Number.isFinite(areaId)) return;
            const caps = a.Capacities && typeof a.Capacities === 'object' ? a.Capacities : {};
            const avail = Object.keys(caps).some((ck) => {
                const c = caps[ck];
                return c && Number(c.quantity) > 0 && Number(c.Value) >= party;
            });
            if (!byArea.has(areaId)) byArea.set(areaId, new Map());
            const cur = byArea.get(areaId).get(time) || false;
            byArea.get(areaId).set(time, cur || avail);
        });
    });
    // Normalize to arrays covering EVERY slot time (fill missing with available:false).
    const out = new Map();
    times.sort();
    byArea.forEach((tmap, areaId) => {
        out.set(areaId, times.map((t) => ({ time: t, available: Boolean(tmap.get(t)) })));
    });
    return { areasAvail: out, times };
}

/*
 * Largest bookable party the live availability can seat: the max Capacity Value (with quantity>0)
 * seen across ALL areas in ALL slots. This is the effective online-booking ceiling the public page
 * uses to route oversized groups to a phone booking (the server still hard-enforces the exact
 * configured threshold). Returns 0 when no capacity is offered at all.
 */
function largestAvailableCapacity(timeSlots) {
    let max = 0;
    (Array.isArray(timeSlots) ? timeSlots : []).forEach((slot) => {
        const areas = slot && slot.Areas && typeof slot.Areas === 'object' ? slot.Areas : {};
        Object.keys(areas).forEach((k) => {
            const a = areas[k];
            const caps = a && a.Capacities && typeof a.Capacities === 'object' ? a.Capacities : {};
            Object.keys(caps).forEach((ck) => {
                const c = caps[ck];
                if (c && Number(c.quantity) > 0) {
                    const v = Number(c.Value);
                    if (Number.isFinite(v) && v > max) max = v;
                }
            });
        });
    });
    return max;
}

export default function PublicBusinessPage() {
    const { t, dateLocale } = useI18n();
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const days = useMemo(() => nextDays(14, t, dateLocale), [t, dateLocale]);
    const todayIdx = new Date().getDay();

    // Pre-fill from /discover search bar query params (?date=YYYY-MM-DD&time=HH:MM&party=N),
    // guarding invalid values against the real day list / sane ranges.
    const qpDate = searchParams.get('date');
    const initDate = useMemo(
        () => (days.some((d) => d.key === qpDate) ? qpDate : days[0].key),
        [qpDate, days],
    );
    const initParty = useMemo(() => {
        const n = parseInt(searchParams.get('party'), 10);
        return Number.isFinite(n) && n >= 1 && n <= 12 ? n : 2;
    }, [searchParams]);
    const initTime = useMemo(() => {
        const t = searchParams.get('time');
        return /^\d{2}:\d{2}$/.test(String(t || '')) ? t : null;
    }, [searchParams]);

    const [biz, setBiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(initDate);
    const [party, setParty] = useState(initParty);
    const [area, setArea] = useState(null);      // chosen area id (or null)
    const [slot, setSlot] = useState(null);
    const [reqTime] = useState(initTime);        // requested time (for the results subline)
    const [timeSlots, setTimeSlots] = useState([]);   // raw publicAvailableTimes result
    const [availLoading, setAvailLoading] = useState(false);
    const [form, setForm] = useState({ phone: '', name: '', notes: '', marketing: false });
    const [step, setStep] = useState('book');    // book | done
    const [booking, setBooking] = useState(false);
    const [bookErr, setBookErr] = useState('');
    const [manageToken, setManageToken] = useState(null); // self-manage link token (from publicBooking)
    const availSeq = useRef(0);                   // guard against out-of-order availability responses

    // Load the real business by slug (public, no-auth).
    useEffect(() => {
        let alive = true;
        (async () => {
            const real = await publicBusinessSvc.getPublicBusiness(slug);
            if (alive) { setBiz(real ? buildBiz(real, slug, t) : null); setLoading(false); }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, t]);

    // Fetch REAL availability whenever the business, date or party changes. Seq-guarded so a
    // slow earlier response can't overwrite a newer one.
    useEffect(() => {
        if (!biz || !date) return undefined;
        let alive = true;
        const seq = ++availSeq.current;
        setAvailLoading(true);
        (async () => {
            const slots = await publicBusinessSvc.getPublicAvailability({ slug, date });
            if (!alive || seq !== availSeq.current) return;
            setTimeSlots(Array.isArray(slots) ? slots : []);
            setAvailLoading(false);
        })();
        return () => { alive = false; };
    }, [biz, slug, date, party]);

    const selectedDay = days.find((d) => d.key === date) || days[0];
    const daySlots = (biz && biz.slotsByDay[selectedDay.weekday]) || [];
    const hasAreas = Boolean(biz && biz.areas && biz.areas.length > 0);

    // areaID → area name map (for showing NAMES, never internal IDs).
    const areaNames = useMemo(() => {
        const m = new Map();
        (biz && biz.areas ? biz.areas : []).forEach((a) => { if (a && a.id != null) m.set(Number(a.id), a.name); });
        return m;
    }, [biz]);

    // Real per-area availability for the current party, from the live TimeSlots.
    const { areasAvail } = useMemo(() => availabilityByArea(timeSlots, party), [timeSlots, party]);

    // Effective online-booking ceiling from the live availability (largest seatable capacity).
    // If the party exceeds it, the group is too large for online → route to a phone booking.
    // Only applied once we actually have availability data (a real ceiling > 0), so an empty/slow
    // load never spuriously blocks; the server still hard-enforces the exact configured threshold.
    const largestCapacity = useMemo(() => largestAvailableCapacity(timeSlots), [timeSlots]);
    const isOversized = largestCapacity > 0 && party > largestCapacity;

    // Build the render list: one section per area (that we have a NAME for), each with its
    // ordered time chips. Areas with zero availability all day are OMITTED (cleaner UX) —
    // if that empties everything, the "no availability" state below shows instead.
    const areaSections = useMemo(() => {
        const out = [];
        areasAvail.forEach((slots, areaId) => {
            const name = areaNames.get(areaId);
            if (!name) return;                                  // never surface an unnamed/internal area
            if (!slots.some((s) => s.available)) return;        // omit all-closed areas
            out.push({ areaId, name, slots });
        });
        // Stable order following biz.areas declaration.
        const order = new Map((biz && biz.areas ? biz.areas : []).map((a, i) => [Number(a.id), i]));
        out.sort((x, y) => (order.get(x.areaId) ?? 0) - (order.get(y.areaId) ?? 0));
        return out;
    }, [areasAvail, areaNames, biz]);

    const hasAvailability = areaSections.length > 0;

    // The selected (area, slot) must STILL be an available chip in the current results — so a
    // party/date change (or query-driven state change) that invalidates a prior pick can't
    // leave a stale, no-longer-available selection submit-enabled. (Server still re-validates.)
    // A valid selection = the chosen (area, slot) is an AVAILABLE chip in the current results.
    // No current availability sections → nothing is bookable, so this is false (never a fallback
    // that could enable booking a slot that isn't actually offered). Server still re-validates.
    const selectedIsAvailable = useMemo(() => {
        if (!slot || area == null) return false;
        const sec = areaSections.find((s) => s.areaId === area);
        return !!(sec && sec.slots.some((s) => s.time === slot && s.available));
    }, [slot, area, areaSections]);

    // Clear any selection the current results no longer offer (party/date recompute, empty state).
    useEffect(() => {
        if (slot && !selectedIsAvailable) { setSlot(''); setArea(null); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsAvailable]);

    // Progressive validation — each field gates the next visual emphasis.
    const okPhone = phoneValid(form.phone);
    const okName = form.name.trim().length > 0;
    const canSubmit = okPhone && okName && selectedIsAvailable;

    // Real submit → publicBooking (server resolves the business from the slug).
    const submitBooking = async () => {
        if (!canSubmit || booking) return;
        setBooking(true); setBookErr('');
        const res = await publicBusinessSvc.publicBooking({
            slug,
            date: selectedDay.key,
            startTime: `${slot}:00`,
            numberOfGuests: party,
            guestName: form.name.trim(),
            guestPhone: form.phone.replace(/[^\d+]/g, ''),   // keep '+' for international (E.164)
            ...(hasAreas && area != null ? { area } : {}),
            guestNotes: form.notes.trim(),
            marketingConsent: form.marketing,
        });
        setBooking(false);
        if (res.ok) { setManageToken(res.manageToken || null); setStep('done'); }
        else if (res.rateLimited) { setBookErr(t('business.rate_limited')); }
        else { setBookErr(t('business.booking_failed')); }
    };

    if (loading) {
        return <PublicShell><div className="pb" style={{ padding: 40, textAlign: 'center' }}>{t('business.loading')}</div></PublicShell>;
    }
    if (!biz) {
        return <PublicShell><div className="pb" style={{ padding: 40, textAlign: 'center' }}>{t('business.not_found')}</div></PublicShell>;
    }

    const hasCover = biz.gallery[0] && biz.gallery[0].filePath;
    const areaName = hasAreas && area != null ? (biz.areas.find((a) => a.id === area) || {}).name : null;

    const summary = (
        <div className="pb-summary">
            <span><Icon name="calendar" size={15} /> <b>{selectedDay.dow}</b> {selectedDay.dnum}</span>
            <span><Icon name="users" size={15} /> <b>{party}</b> {t('business.summary_diners')}</span>
            {slot && <span><Icon name="clock" size={15} /> <b>{slot}</b></span>}
            {areaName && <span><Icon name="store" size={15} /> {areaName}</span>}
        </div>
    );

    return (
        <PublicShell>
            <div className="pb">
                {/* Bold FLAT hero — solid pink, or gallery image + flat dark overlay */}
                <motion.header
                    className={`pb-hero${hasCover ? ' pb-hero--photo' : ''}`}
                    style={hasCover
                        ? { backgroundImage: `linear-gradient(rgba(15,10,14,.55), rgba(15,10,14,.55)), url(${biz.gallery[0].filePath})` }
                        : undefined}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}
                >
                    <Link to="/discover" className="pb-back"><Icon name="chevron-right" size={16} /> {t('business.back')}</Link>
                    <div className="pb-hero-body">
                        <h1 className="pb-hero-name">{biz.name}</h1>
                        <div className="pb-hero-meta">
                            {biz.cuisine && <span>{biz.cuisine}</span>}
                            {biz.cuisine && biz.area && <span className="sep">·</span>}
                            {biz.area && <span><Icon name="store" size={14} /> {biz.area}</span>}
                        </div>
                    </div>
                </motion.header>

                {/* ── GALLERY band (flat, sharp) — horizontal scroll on mobile, only if images ── */}
                {biz.gallery.length > 0 && (
                    <section className="pb-gallery-band" aria-label={t('business.gallery_aria')}>
                        <div className="pb-gallery-strip">
                            {biz.gallery.map((g, i) => (
                                <div className="pb-gallery-tile" key={i}>
                                    <img src={g.filePath} alt={g.caption || t('business.image_alt')} loading="lazy" />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <div className="pb-layout">
                    {/* ── BOOKING panel (primary) ── */}
                    <aside className="pb-book" aria-label={t('business.booking_aria')}>
                        <div className="pb-book-card">
                            {step !== 'done' && (
                                <div className="pb-book-head">
                                    <h2>{t('business.booking_title')}</h2>
                                    <p>{t('business.booking_sub')}</p>
                                </div>
                            )}

                            {step === 'book' && (
                                <form onSubmit={(e) => { e.preventDefault(); submitBooking(); }}>
                                    {/* Step 1 — PHONE first */}
                                    <div className="pb-step">
                                        <span className="pb-step-badge">1</span>
                                        <div className="pb-step-body">
                                            <label htmlFor="bk-phone" className="pb-field-label">{t('business.phone_label')} <span className="req">{t('business.required')}</span></label>
                                            <input id="bk-phone" type="tel" inputMode="tel" dir="ltr" autoComplete="tel"
                                                className={`pb-input${form.phone && !okPhone ? ' invalid' : ''}`}
                                                value={form.phone} placeholder={t('business.phone_placeholder')}
                                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                                            {form.phone && !okPhone && <span className="pb-hint-err">{t('business.phone_invalid')}</span>}
                                        </div>
                                    </div>

                                    {/* Step 2 — NAME */}
                                    <div className="pb-step">
                                        <span className="pb-step-badge">2</span>
                                        <div className="pb-step-body">
                                            <label htmlFor="bk-name" className="pb-field-label">{t('business.name_label')} <span className="req">{t('business.required')}</span></label>
                                            <input id="bk-name" type="text" autoComplete="name"
                                                className="pb-input" value={form.name} placeholder={t('business.name_placeholder')}
                                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                                        </div>
                                    </div>

                                    {/* Step 3 — party / date / area / time */}
                                    <div className="pb-step">
                                        <span className="pb-step-badge">3</span>
                                        <div className="pb-step-body">
                                            <span className="pb-field-label">{t('business.party_label')}</span>
                                            <div className="pb-stepper" role="group" aria-label={t('business.party_label')}>
                                                <button type="button" onClick={() => { setParty((p) => Math.max(1, p - 1)); setSlot(null); setArea(null); }} disabled={party <= 1} aria-label={t('business.dec_aria')}><Icon name="minus" size={16} /></button>
                                                <span className="count">{party}<small>{party === 1 ? t('business.diner_one') : t('business.diner_many')}</small></span>
                                                <button type="button" onClick={() => { setParty((p) => Math.min(12, p + 1)); setSlot(null); setArea(null); }} disabled={party >= 12} aria-label={t('business.inc_aria')}><Icon name="plus" size={16} /></button>
                                            </div>

                                            <span className="pb-field-label">{t('business.date_label')}</span>
                                            <div className="pb-dates">
                                                {days.map((d) => (
                                                    <button key={d.key} type="button" className="pb-date"
                                                        aria-pressed={date === d.key}
                                                        onClick={() => { setDate(d.key); setSlot(null); setArea(null); }}>
                                                        <span className="dow">{d.dow}</span>
                                                        <span className="dnum">{d.dnum}</span>
                                                    </button>
                                                ))}
                                            </div>

                                            <span className="pb-field-label">{t('business.avail_label')} <span className="req">{t('business.required')}</span></span>
                                            {availLoading ? (
                                                <p className="pb-note">{t('business.checking_avail')}</p>
                                            ) : isOversized ? (
                                                <div className="pb-avail">
                                                    <div className="pb-avail-head">
                                                        <h3 className="pb-avail-title">{t('business.oversized_title')}</h3>
                                                        <p className="pb-avail-sub">
                                                            {biz.phone
                                                                ? t('business.oversized_sub_phone')
                                                                : t('business.oversized_sub_nophone')}
                                                        </p>
                                                    </div>
                                                    {biz.phone && (
                                                        <a className="pb-btn-primary" href={`tel:${biz.phone}`} dir="ltr"
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)', textDecoration: 'none' }}>
                                                            <Icon name="phone" size={16} /> {biz.phone}
                                                        </a>
                                                    )}
                                                </div>
                                            ) : hasAvailability ? (
                                                <div className="pb-avail">
                                                    <div className="pb-avail-head">
                                                        <h3 className="pb-avail-title">{t('business.avail_found')}</h3>
                                                        <p className="pb-avail-sub">
                                                            {t('business.avail_found_sub', {
                                                                date: locDate(selectedDay.key, dateLocale),
                                                                time: reqTime ? t('business.avail_around', { time: reqTime }) : '',
                                                                party,
                                                            })}
                                                        </p>
                                                    </div>
                                                    {areaSections.map((sec) => (
                                                        <div className="pb-avail-area" key={sec.areaId}>
                                                            <h4 className="pb-avail-area-name">{sec.name}</h4>
                                                            <div className="pb-avail-slots">
                                                                {sec.slots.map((s) => (
                                                                    s.available ? (
                                                                        <button key={s.time} type="button"
                                                                            className="pb-avail-chip"
                                                                            aria-pressed={slot === s.time && area === sec.areaId}
                                                                            onClick={() => { setArea(sec.areaId); setSlot(s.time); }}>
                                                                            <span className="t">{s.time}</span>
                                                                            <span className="tag">{t('business.instant_confirm')}</span>
                                                                        </button>
                                                                    ) : (
                                                                        <span key={s.time} className="pb-avail-chip is-closed" aria-disabled="true">
                                                                            <span className="t">{s.time}</span>
                                                                            <span className="tag">{t('business.closed')}</span>
                                                                        </span>
                                                                    )
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : daySlots.length === 0 ? (
                                                <p className="pb-note">{t('business.no_hours_today')}</p>
                                            ) : (
                                                <p className="pb-note">{t('business.no_avail_today')}</p>
                                            )}

                                            <label htmlFor="bk-notes" className="pb-field-label">{t('business.notes_label')} <span className="pb-optional">{t('business.optional')}</span></label>
                                            <textarea id="bk-notes" className="pb-input pb-textarea" value={form.notes}
                                                placeholder={t('business.notes_placeholder')}
                                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                                        </div>
                                    </div>

                                    {/* Separate marketing consent — un-pre-checked opt-in (privacy: never assumed from a booking). */}
                                    <label className="pb-consent">
                                        <input type="checkbox" checked={form.marketing}
                                            onChange={(e) => setForm((f) => ({ ...f, marketing: e.target.checked }))} />
                                        <span>{t('business.marketing_consent')}</span>
                                    </label>

                                    {slot && <div className="pb-summary-wrap">{summary}</div>}
                                    {bookErr && <p className="pb-book-err">{bookErr}</p>}

                                    <button type="submit" className="pb-btn-primary" disabled={!canSubmit || booking}>
                                        {booking ? t('business.submitting') : t('business.submit')}
                                    </button>
                                    {!canSubmit && !booking && (
                                        <p className="pb-cta-hint">
                                            {!okPhone ? t('business.hint_phone')
                                                : !okName ? t('business.hint_name')
                                                    : !selectedIsAvailable ? t('business.hint_slot_area')
                                                        : t('business.hint_slot')}
                                        </p>
                                    )}
                                </form>
                            )}

                            {step === 'done' && (
                                <div className="pb-success">
                                    <div className="check" aria-hidden="true"><Icon name="check" size={22} /></div>
                                    <h3>{t('business.success_title')}</h3>
                                    <p>{t('business.success_sub')}</p>
                                    {summary}
                                    {manageToken && (
                                        <div className="pb-manage-link">
                                            <span>{t('business.manage_prompt')}</span>
                                            <Link to={`/r/${manageToken}`} className="pb-btn-ghost pb-btn-link">{t('business.manage_link')}</Link>
                                        </div>
                                    )}
                                    <button type="button" className="pb-btn-ghost"
                                        onClick={() => { setStep('book'); setSlot(null); setForm({ phone: '', name: '', notes: '' }); setArea(null); setManageToken(null); }}>
                                        {t('business.another_booking')}
                                    </button>
                                    <Link to="/discover" className="pb-btn-ghost pb-btn-link">{t('common.back_to_discovery')}</Link>
                                </div>
                            )}
                        </div>
                    </aside>

                    {/* ── Content ── */}
                    <div className="pb-content">
                        {biz.about && (
                            <section className="pb-section" aria-label={t('business.about_aria')}>
                                <h2 className="pb-section-title">{t('business.about_title')}</h2>
                                <p className="pb-about-text">{biz.about}</p>
                            </section>
                        )}

                        {biz.menu.length > 0 && (
                            <section className="pb-section" aria-label={t('business.menu_aria')}>
                                <h2 className="pb-section-title">{t('business.menu_title')}</h2>
                                {biz.menu.map((c) => (
                                    <div className="pb-menu-cat" key={c.cat}>
                                        <h3>{c.cat}</h3>
                                        {c.dishes.map((d) => (
                                            <div className="pb-dish" key={d.name}>
                                                <span className="pb-dish-name">{d.name}{d.desc && <span className="pb-dish-desc">{d.desc}</span>}</span>
                                                {d.price > 0 && <span className="pb-dish-price">{currencySymbol(biz.currency)}{d.price}</span>}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </section>
                        )}

                        <section className="pb-section" aria-label={t('business.hours_aria')}>
                            <h2 className="pb-section-title">{t('business.hours_title')}</h2>
                            {biz.hours.map((w, i) => (
                                <div className={`pb-hours-row${i === todayIdx ? ' today' : ''}`} key={w.day}>
                                    <span className="day">{w.day}{i === todayIdx ? t('business.hours_today_suffix') : ''}</span>
                                    <span className="time">{w.time}</span>
                                </div>
                            ))}
                        </section>

                        {biz.area && (
                            <section className="pb-section" aria-label={t('business.location_aria')}>
                                <h2 className="pb-section-title">{t('business.location_title')}</h2>
                                <div className="pb-detail"><span className="ico" aria-hidden="true"><Icon name="store" size={18} /></span><span>{biz.area}</span></div>
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </PublicShell>
    );
}
